/**
 * Ce se randeaza pe pagina «Acasă» — partea PURA (fara React), ca sa fie testabila in vitest (mediu `node`).
 *
 * Regula unica a fisierului: **o sectiune fara nimic de aratat nu se randeaza.** Un link fara URL, sau o
 * scurtatura pe care rolul nu o poate deschide, ar fi un ecran care minte (buton mort, sau 403 dupa tap).
 * Aceeasi regula ca la `RandContactEcotic` (`null` cand nu e nimic).
 */

import type { UserInfo } from '../../../providers/auth-provider.types';
import type { NavLinksType } from '../../../providers/routes-provider.types';
import { puncteText, termenExpirat, ziSiLuna } from '../../lessons/features/practical/practical-evidence';

export interface LinkDocument {
  eticheta: string;
  href: string;
}

export interface DocumenteEditie {
  regulament?: string | null;
  mecanism?: string | null;
}

/**
 * Doar Regulamentul si Mecanismul, cum cere planul. `useEditionDocuments()` mai intoarce protocolul si
 * prezentarea colaboratorilor — nu apar aici. Un URL gol nu produce link.
 */
export function documenteAcasa(docs: DocumenteEditie): LinkDocument[] {
  const lista: LinkDocument[] = [];
  if (docs.regulament) lista.push({ eticheta: 'Regulamentul Campaniei', href: docs.regulament });
  if (docs.mecanism) lista.push({ eticheta: 'Mecanismul ediției', href: docs.mecanism });
  return lista;
}

/** Ce citeste anuntul dintr-o provocare pentru scoala (campurile din `LessonDto`). */
export interface ProvocarePentruAnunt {
  id?: string;
  title?: string;
  image?: string;
  /** String ISO fara zona, desi tipul generat spune `Date`. */
  deadline?: string | Date | null;
  punctajMaxim?: number | bigint | null;
  isHidden?: boolean;
}

export interface AnuntProvocare {
  id: string;
  intro: string;
  titlu: string;
  detalii: string;
  imagine?: string;
}

/**
 * Anuntul de pe «Acasă» al profesorului (cerinta ECOTIC, 2026-10-02): prima provocare pentru scoala
 * vizibila si inca in termen. Fara una, nu se randeaza nimic — anuntul dispare singur dupa termen sau
 * cand provocarea e ascunsa. Titlul, termenul si punctele vin din provocare, deci urmatoarea nu cere cod.
 *
 * Mereu „O nouă provocare": profesorul primeste doar provocarile vizibile (LessonService), deci o
 * provocare trecuta si ascunsa nu se poate numara aici. „Prima provocare" ar fi aparut si la a doua
 * (EWWR, 2026-11-03), cand E-WASTE DAY era deja ascunsa — decizia owner-ului.
 */
export function anuntProvocare(
  provocari: ReadonlyArray<ProvocarePentruAnunt> | null | undefined,
  now: Date = new Date()
): AnuntProvocare | null {
  const vizibile = (provocari ?? []).filter((p) => p.id && p.title?.trim() && p.isHidden !== true);
  const activa = vizibile.find((p) => !termenExpirat(p.deadline, now));
  if (!activa) return null;

  const zi = ziSiLuna(activa.deadline);
  const puncte = puncteText(activa.punctajMaxim);
  const panaPe = zi ? ` până pe ${zi}` : '';
  const detalii = puncte
    ? `Încarcă dovezi pentru această activitate${panaPe} și școala ta poate primi ${puncte}, după verificarea și validarea activității.`
    : `Încarcă dovezi pentru această activitate${panaPe}.`;

  return {
    id: activa.id as string,
    intro: 'O nouă provocare pentru școala ta este acum disponibilă în secțiunea Provocări pentru Școli!',
    titlu: (activa.title as string).trim(),
    detalii,
    imagine: activa.image || undefined,
  };
}

export interface Scurtatura {
  eticheta: string;
  cale: string;
}

/**
 * Candidatii pe rol: NUMELE paginii din `PAGES` (nu o cale scrisa de mana, nu cheia din `NAV_LINKS_V2`).
 * 🔴 Cheile din `NAV_LINKS_V2` sunt VALORILE lui `PAGES`, iar ele difera de nume la cel putin o pagina:
 * `PAGES.PRACTICAL_ACTIVITY_LISTING === "PRACTICAL_ACTIVITY"`. Cautat direct dupa nume, «Activități» nu aparea
 * NICIODATA, fara nicio eroare — sonda a prins-o, testele scrise inainte nu (fixtura avea numele = valoarea).
 *
 * PROFESOR nu are scurtaturi: pagina lui e `AcasaProfesor` (macheta owner-ului, 21.09).
 */
export const CANDIDATI_SCURTATURI: Record<string, ReadonlyArray<{ eticheta: string; pagina: string }>> = {
  ELEV: [
    { eticheta: 'Exerciții pentru acasă', pagina: 'HOMEWORK_LISTING' },
    { eticheta: 'Provocări', pagina: 'MONTHLY_CHALLENGES_LISTING' },
  ],
};

/**
 * Scurtaturile pe care ROLUL CURENT chiar le poate deschide: pagina exista in `NAV_LINKS_V2`, rolul e in
 * `ALLOWED_ROLES`, iar `SHOW_CONDITION` (daca are) e adevarata pentru acest user — aceleasi doua conditii
 * pe care le aplica si sidebar-ul. «Puncte» nu exista pentru ELEV (`SCHOOL_POINTS` e doar PROFESOR), deci
 * nu se randeaza pentru el.
 */
export function scurtaturiAcasa(
  user: UserInfo | null | undefined,
  navLinks: NavLinksType,
  pages: Record<string, string>
): Scurtatura[] {
  const rol = user?.role;
  if (!rol) return [];
  const candidati = CANDIDATI_SCURTATURI[rol] ?? [];
  const lista: Scurtatura[] = [];
  for (const { eticheta, pagina } of candidati) {
    const link = navLinks[pages[pagina]];
    if (!link) continue;
    if (!link.ALLOWED_ROLES.includes(rol)) continue;
    if (link.SHOW_CONDITION && !link.SHOW_CONDITION(user)) continue;
    lista.push({ eticheta, cale: link.PATH });
  }
  return lista;
}

/**
 * Item 11 (21.09): cand backendul confirma ca protocolul e deja pe editia curenta, toata zona de
 * descarcare/incarcare de pe Acasa dispare -- ramane doar in Profil, pentru corectii ulterioare.
 * Aceeasi regula ca la `documenteAcasa`: o sectiune fara nimic de facut nu se randeaza.
 */
export function protocolulSeArataPeAcasa(
  profil: { protocolInEditiaCurenta?: boolean } | null | undefined
): boolean {
  return profil?.protocolInEditiaCurenta !== true;
}

export interface ProfilPentruInlocuireProtocol {
  nume?: string | null;
  telefon?: string | null;
  email?: string | null;
}

/** Cauza reala, afisata INAINTE de upload, cand backendul ar respinge oricum salvarea. */
export const MESAJ_TELEFON_LIPSA_PROTOCOL = 'Completează telefonul în Profil înainte de a încărca protocolul.';

/**
 * De ce nu se poate inlocui protocolul din Acasa, sau `null` daca se poate. Se verifica INAINTE de
 * upload, ca profesorul sa nu incarce un fisier care apoi nu se mai poate salva.
 *
 * Doar telefonul LIPSA blocheaza, nu cel cu format nevalid: `PATCH /api/users/profile` e PATCH real
 * (un camp absent nu se schimba — `UserService.updateProfile`), dar dupa merge
 * `UserValidator.validateUpdate` cere telefon nevid pe PROFESOR, oricare ar fi corpul. Un telefon
 * vechi cu alt format trece, pentru ca nu-l mai trimitem (vezi `corpInlocuireProtocol`).
 */
export function motivBlocareInlocuireProtocol(
  profil: ProfilPentruInlocuireProtocol | null | undefined
): string | null {
  if (!profil) return 'Profilul nu s-a încărcat încă. Reîncarcă pagina și încearcă din nou.';
  if (!profil.telefon?.trim()) return MESAJ_TELEFON_LIPSA_PROTOCOL;
  if (!profil.nume?.trim() || !profil.email?.trim()) {
    return 'Completează numele și emailul în Profil înainte de a încărca protocolul.';
  }
  return null;
}

/**
 * Corpul MINIMAL al `PATCH /api/users/profile` cand profesorul inlocuieste protocolul din Acasa:
 * doar ce cere DTO-ul ca obligatoriu (`nume`, `email` — `@NotBlank` pe `UserProfileDTO`) plus
 * `protocolDeColaborare`, care INLOCUIESTE lista (un singur URL), nu adauga la ea — spre deosebire
 * de `useProfileWithForm().handleFileUpload`, gandit pentru Profil.
 *
 * Nu mai trece prin `buildProfilePayload`: acela revalida TOT profilul, deci un telefon vechi cu alt
 * format bloca inlocuirea protocolului dupa ce fisierul era deja incarcat. Campurile netrimise raman
 * neschimbate in backend (PATCH), deci nu se pierde nimic.
 */
export function corpInlocuireProtocol(
  profil: ProfilPentruInlocuireProtocol | null | undefined,
  urlNou: string
): Record<string, unknown> {
  return {
    nume: profil?.nume ?? '',
    email: profil?.email ?? '',
    protocolDeColaborare: [urlNou],
  };
}
